'use client';

import { faBookmark as faBookmarkRegular } from '@fortawesome/free-regular-svg-icons';
import { faBookmark as faBookmarkSolid } from '@fortawesome/free-solid-svg-icons';
import { Button } from '@uzh-bf/design-system';
import { useParams } from 'next/navigation';
import { useEffect, useState } from 'react';
import { mutate } from 'swr';
import useSWRMutation from 'swr/mutation';
import { twMerge } from 'tailwind-merge';
import { useCachedBookmarkSWR } from '~/app/actions/progress';
import { bookmarkBlock } from '~/app/actions/unitProgress.server';
import { CACHE_KEYS } from '~/lib/cache';
import { useAuthToken } from '~/lib/hooks';
import { trackEvent } from '~/lib/matomo';
import ActionTrigger from './ActionTrigger';
function BookmarkAction({
  moduleId_,
  unitId_,
  blockIdentifier
}: {
  moduleId_?: string;
  unitId_?: string;
  blockIdentifier?: string;
}) {
  const jwt = useAuthToken();
  const params = useParams<{
    locale: string;
    courseId: string;
    moduleId: string;
    unitId: string;
    blockIdent: string;
  }>();
  const doesExist = !!(moduleId_ && unitId_ && blockIdentifier);
  const {
    courseId,
    moduleId,
    unitId,
    blockIdent
  } = params;
  moduleId_ = moduleId_ || moduleId;
  unitId_ = unitId_ || unitId;
  blockIdentifier = blockIdentifier || blockIdent;
  const {
    data: serverBookmarkExists,
    isLoading
  } = useCachedBookmarkSWR({
    courseId: courseId,
    moduleId: moduleId_,
    unitId: unitId_,
    blockIdent: blockIdentifier
  });

  // Local state for INSTANT UI updates - this is what the user sees immediately
  const [localBookmarkExists, setLocalBookmarkExists] = useState(!!serverBookmarkExists);

  // Sync local state with server state when data loads/changes
  useEffect(() => {
    setLocalBookmarkExists(!!serverBookmarkExists);
  }, [serverBookmarkExists]);
  const {
    trigger
  } = useSWRMutation('bookmarkBlock', bookmarkBlock, {
    // No optimistic updates needed - we handle UI with local state for instant feedback
  });
  if (blockIdentifier === 'overview' || blockIdentifier === 'summary') {
    return null;
  }
  return <Button disabled={isLoading} onClick={e => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (!jwt) {
      console.warn('[BookmarkAction] No JWT, skipping');
      return;
    }
    const newValue = !localBookmarkExists;
    trackEvent({
      category: 'bookmark',
      action: 'toggle',
      name: newValue ? 'on' : 'off'
    });

    // STEP 1: Update UI IMMEDIATELY - user sees instant feedback (0ms)
    setLocalBookmarkExists(newValue);

    // STEP 2: Optimistically update bookmarks list cache for instant count badge update
    mutate(CACHE_KEYS.BOOKMARK({
      courseId: courseId
    }), async (currentData: unknown) => {
      if (!currentData || !Array.isArray(currentData)) return currentData;

      // If adding bookmark, add a placeholder entry
      // If removing, filter out the bookmark
      if (newValue) {
        return [...currentData, {
          blockIdentifier
        }];
      } else {
        return currentData.filter((b: {
          blockIdentifier?: string;
        }) => b?.blockIdentifier !== blockIdentifier);
      }
    }, {
      revalidate: false
    } // Don't revalidate yet, wait for mutation
    );

    // STEP 3: Fire mutation in background - user doesn't wait for this
    trigger({
      newValue,
      courseId: Number(courseId),
      moduleId: Number(moduleId_),
      unitId: Number(unitId_),
      blockIdentifier: blockIdentifier,
      jwt
    }).then(() => {
      // Revalidate all bookmark caches to sync with server truth
      mutate((key: string | undefined) => key?.includes(CACHE_KEYS.BOOKMARK({
        courseId: courseId
      })));
    }).catch(error => {
      console.error('[BookmarkAction] Failed to update bookmark:', error);
      // Rollback UI on error - restore previous state
      setLocalBookmarkExists(!newValue);

      // Rollback cache as well
      mutate((key_0: string | undefined) => key_0?.includes(CACHE_KEYS.BOOKMARK({
        courseId: courseId
      })));
    });
  }} className={{
    root: twMerge('rounded-full w-8 h-8 text-primary-100 bg-white', doesExist && 'rounded-none border-none p-0 bg-transparent')
  }}>
      <ActionTrigger icon={localBookmarkExists ? faBookmarkSolid : faBookmarkRegular} className={twMerge(localBookmarkExists && 'text-primary-100')} />
    </Button>;
}
export default BookmarkAction;